/**
 * Hard gate: the app shell requires a signed-in session. Public routes
 * (/login, /privacy, /terms, /diario-quirurgico, /surgical-logbook, /features)
 * live outside this wrapper.
 *
 * One exception: at `/` a signed-out visitor gets `signedOutFallback` (the
 * public landing page) instead of a redirect, so the URL people search for
 * and share resolves to actual content. See lib/public-routes.ts.
 */
import { Navigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { LOGIN_PATH } from '@/const';
import { BootSplash } from '@/components/BootSplash';
import { Button } from '@/components/Button';
import { likelyReturningAccount } from '@/lib/auth-shell-hint';
import { rendersPublicLanding } from '@/lib/public-routes';

export function RequireAuth({
  children,
  signedOutFallback,
}: {
  children: React.ReactNode;
  signedOutFallback?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, error, refresh } = useAuth();
  const location = useLocation();

  // While bootstrap loads: anonymous visitors on `/` get the landing immediately
  // (PageSpeed LCP). Returning accounts keep the splash so the dashboard does
  // not flash marketing chrome. Other routes still wait on the full shell.
  if (isLoading) {
    if (
      signedOutFallback &&
      rendersPublicLanding(location.pathname) &&
      !likelyReturningAccount()
    ) {
      return <>{signedOutFallback}</>;
    }
    return <BootSplash />;
  }

  // Network/server failure while checking the session — do not pretend the
  // user is logged out (that sends them to login and can wipe context).
  if (error && !isAuthenticated) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4">
        <div className="w-full max-w-[400px] rounded-card border border-linea bg-superficie p-6 text-center">
          <p className="font-serif text-[18px] text-tinta">{t('common.sessionError.title')}</p>
          <p className="mt-2 text-[13px] leading-5 text-tinta-2">
            {t('common.sessionError.body')}
          </p>
          <Button
            className="mt-5 w-full"
            onClick={() => {
              void refresh();
            }}
          >
            {t('common.sessionError.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (signedOutFallback && rendersPublicLanding(location.pathname)) {
      return <>{signedOutFallback}</>;
    }
    return <Navigate to={LOGIN_PATH} replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
